import React from "react";
import { Button, Message } from "semantic-ui-react";
import { LabelledRow } from "../components";
import ValidatorContext from "../formvalidation/FormContext";
import TwoColumnLayout from "../layout/TwoColumnLayout";
import CostCenterOptionsPanel from "./CostCenterOptionsPanel";
import { WarnUnsavedChanges } from "components/WarnUnsavedChanges/WarnUnsavedChanges";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import RightSideNavigation from "components/RightSideNavigation/RightSideNavigation";
import { useValidators } from "formvalidation/hooks";
import EditViewPanel from "components/panels/EditViewPanel";
import CostCenterActivityPanel from "./ActivityPanel/App";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import { useLoaderData } from "react-router";

const CostCenterEdit = () => {
  const viewModel = useLoaderData<server.viewmodel.CostCenterEditFrontModel>();

  const [title, setTitle] = React.useState(viewModel.costCenter?.title ?? "");
  const [internalId, setInternalId] = React.useState(viewModel.costCenter?.internalId ?? undefined);
  const [isLoading, setIsLoading] = React.useState(false);
  const [visible, setVisible] = React.useState(viewModel.costCenter?.visible ?? true);
  const [errors, setErrors] = React.useState<string[]>([]);
  const validators = useValidators();

  const submit = async () => {
    setIsLoading(true);
    setErrors([]);

    const validationResult = await validators.validateAll();
    if (validationResult.hasErrors) {
      setIsLoading(false);
      return;
    }

    let errors: string[] = [];

    if (viewModel.costCenter == null) {
      const res = await window.CoreApi.CostCenter.CreateCostCenter({
        title,
        internalId,
        visible
      });

      errors = res.data.errors.map(e => e.message);
    } else {
      const res = await window.CoreApi.CostCenter.UpdateCostCenter({
        costCenterId: viewModel.costCenter.costCenterId,
        title,
        internalId,
        visible
      });
      errors = res.data.errors.map(e => e.message);
    }

    if (errors.length > 0) {
      setErrors(errors);
      setIsLoading(false);
      return;
    }

    window.location.href = viewModel.listUrl;
  };

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <TwoColumnLayout>
        <WarnUnsavedChanges state={{ title, internalId }} />
        <TwoColumnLayout.LeftColumn>
          <EditViewPanel title={viewModel.pageTitle}>
            <ValidatorContext settings={validators.validatorSettings}>
              <table className="s1_table bordergrid gray">
                <tbody>
                  <LabelledRow label={RESX.GeneralLabels.Title}>
                    <RequiredTextInput
                      value={title}
                      onChange={setTitle}
                      validatorKey="title"
                      maxLength={serverReference.constants.costCenter.maxTitleLength}
                      disabled={!viewModel.canModifyTitle}
                    />
                  </LabelledRow>

                  <LabelledRow label={RESX.GeneralLabels.InternalCode}>
                    <RequiredTextInput
                      value={internalId}
                      onChange={setInternalId}
                      validatorKey="internalId"
                      maxLength={serverReference.constants.costCenter.maxInternalIdLength}
                      disabled={!viewModel.canModifyInternalId}
                    />
                  </LabelledRow>

                  <LabelledRow label={RESX.GeneralLabels.Visible}>
                    <Checkbox
                      checked={visible}
                      onChange={() => { setVisible(!visible); }}
                    />
                  </LabelledRow>
                </tbody>
              </table>

              {errors.length > 0 && <Message className="margin-top" color="red">
                {errors.map(error => <div key={error}>{error}</div>)}
              </Message>}

              <div className="flex margin-top-bigger">
                <div className="grow1"></div>
                <Button
                  primary
                  onClick={submit}
                  loading={isLoading}
                  disabled={isLoading}
                >{RESX.Buttons.btnSave}</Button>
              </div>
            </ValidatorContext>
          </EditViewPanel>
        </TwoColumnLayout.LeftColumn>
        <TwoColumnLayout.RightColumn>
          <CostCenterOptionsPanel
            optionsPanelData={viewModel.optionsPanel}
            listUrl={viewModel.listUrl}
            existingCostCenter={viewModel.costCenter}
          />
          <CostCenterActivityPanel
            config={viewModel.activityPanel}
            itemId={viewModel.costCenter?.costCenterId}
          />
        </TwoColumnLayout.RightColumn>
      </TwoColumnLayout>
    </TwoColumnLayout.LeftColumn>
    <RightSideNavigation
      title={desktopNavigation.rightSideNavigation.title}
      menuItems={desktopNavigation.rightSideNavigation.items}
      currentPage={desktopNavigation.currentPage}
    />
  </TwoColumnLayout>;
};

export default CostCenterEdit;